/**
 * DispatchHistory · v2.6.9 batch BJ-6
 * ====================================
 * Rolling list of the last 10 dispatch events for night-shift dispatchers.
 * Pulls from localStorage (populated by `useFleetStream` whenever a
 * `driver_dispatched` WS event arrives) AND subscribes to the same custom
 * DOM event for live updates while this component is mounted.
 *
 * Each row shows time + badge pill + trip_id + route + a "View on Map" link
 * that switches the top-level tab to "live" AND emits `rtg:focus_driver`
 * with the cached last_lat/last_lng so the LiveDispatchMap pans to that
 * driver's marker without waiting for the next GPS ping.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { History, MapPin, RotateCw } from "lucide-react";

const STORAGE_KEY = "rtg_dispatch_history";

function readHistory() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}

/**
 * @param {object} props
 * @param {(tab: string) => void} props.onJumpToLiveMap  optional callback to
 *   change the AdminDashboard top-level tab to "live" when "View on Map" is clicked.
 */
export default function DispatchHistory({ onJumpToLiveMap }) {
  const [items, setItems] = useState(() => readHistory());

  useEffect(() => {
    const onDispatched = (e) => {
      setItems((prev) => [e.detail, ...prev].slice(0, 10));
    };
    window.addEventListener("rtg:driver_dispatched", onDispatched);
    return () => window.removeEventListener("rtg:driver_dispatched", onDispatched);
  }, []);

  const clearAll = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setItems([]);
  };

  const viewOnMap = (item) => {
    // Send focus event first so the LiveDispatchMap (which mounts on tab switch)
    // can read the last_lat/lng and pan there.
    const payload = {
      driver_id: item.driver_id,
      lat: item.last_lat,
      lng: item.last_lng,
    };
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("rtg:focus_driver", { detail: payload }));
    }
    if (onJumpToLiveMap) onJumpToLiveMap("live");
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="dispatch-history-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-base flex items-center gap-2"><History className="h-4 w-4"/>Dispatch History</h3>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Last 10</span>
          {items.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              data-testid="dispatch-history-clear-btn"
              onClick={clearAll}
              className="bg-zinc-950 border-zinc-700 h-7 text-[11px]"
            >
              <RotateCw className="h-3 w-3 mr-1"/>Clear
            </Button>
          )}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="dispatch-history-empty">
          No dispatches yet — when a trip is assigned to a driver it appears here in real time.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="dispatch-history-list">
          {items.map((it, idx) => (
            <li
              key={`${it.trip_id}-${it.ts}-${idx}`}
              className="bg-zinc-950 border border-zinc-800 rounded-md p-3 flex items-start gap-3"
              data-testid="dispatch-history-row"
            >
              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                <Badge className="bg-amber-400 text-zinc-950 font-extrabold text-[10px] tracking-wide" data-testid="dispatch-history-badge">
                  {it.badge || "??"}
                </Badge>
                <span className="text-[10px] text-zinc-500 whitespace-nowrap">{fmtTime(it.ts)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-zinc-200">
                  {it.name || "Driver"} <span className="text-zinc-500">· {it.trip_id}</span>
                </div>
                {it.origin || it.destination ? (
                  <div className="text-[11px] text-zinc-400 mt-0.5 truncate">
                    <span className="text-zinc-300">{it.origin || "—"}</span>
                    <span className="text-amber-400 mx-1">→</span>
                    <span className="text-zinc-300">{it.destination || "—"}</span>
                  </div>
                ) : null}
              </div>
              {it.last_lat != null && it.last_lng != null && (
                <button
                  type="button"
                  onClick={() => viewOnMap(it)}
                  data-testid="dispatch-history-view-on-map"
                  className="text-[11px] text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1 flex-shrink-0 self-center"
                >
                  <MapPin className="h-3 w-3"/>View on Map
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
